import $ from 'jquery';
import _ from 'lodash';

/**
 * Options for the constructor
 */
interface FlyoutRendererOptions {
  /**
   * If false, applies uniform styling to text. Otherwise, it inherits text styling
   * from page CSS (usually Styleguide).
   * Default: true.
   */
  inheritTextStyle?: boolean;
}

/**
 * Options for rendering
 */
export interface RenderFlyoutOptions {
  content: string;
  dark: boolean;
  element: HTMLElement;
  flyoutOffset: {
    left: number;
    top: number;
  };
  belowTarget: boolean;
  rightSideHint: boolean;
}

/**
 * Common flyout renderer.
 * constructorOptions - Options hash, optional.
 *   Keys:
 *     * inheritTextStyle:
 *       If false, applies uniform styling to text. Otherwise, it inherits text styling
 *       from page CSS (usually Styleguide).
 *       Default: true.
 *
 */
function FlyoutRenderer(constructorOptions?: FlyoutRendererOptions) {
  const FLYOUT_WINDOW_PADDING = 22;
  const FLYOUT_BOTTOM_PADDING = 1;
  const FLYOUT_TOP_PADDING = 10;

  const _window = $(window);
  // Popover API puts the element in the browser top layer, above forge-dialog
  // backdrops. Falls back to visibility class for older browsers.
  const supportsPopover = 'showPopover' in document.createElement('div');
  const showFlyoutElement = () => (supportsPopover ? _flyout[0].showPopover() : _flyout.addClass('visible'));
  const hideFlyoutElement = () =>
    supportsPopover ? _flyout[0].hidePopover() : _flyout.removeClass('visible');
  let _flyout: JQuery;
  let _flyoutContent: JQuery;
  let _flyoutHint: JQuery;
  let showFlyoutTimer: number | null = null; // Added for delay tracking

  constructorOptions = _.merge(
    {
      inheritTextStyle: true,
    },
    constructorOptions,
  );

  // Defer render until the document is ready.
  // Note that this is always asynchronous as of jQuery 3.4.1
  $(function () {
    _renderFlyoutTemplate();
    _hideFlyout();
  });

  /**
   * Public methods
   */

  this.render = function (options: RenderFlyoutOptions) {
    // Clear any existing timer when new render is called
    if (showFlyoutTimer) {
      clearTimeout(showFlyoutTimer);
      showFlyoutTimer = null;
    }
    _renderFlyoutData(options);
  };

  this.clear = function () {
    // Clear any pending timer when clearing the flyout
    if (showFlyoutTimer) {
      clearTimeout(showFlyoutTimer);
      showFlyoutTimer = null;
    }
    _hideFlyout();
  };

  /**
   * Private methods
   */

  function _renderFlyoutTemplate() {
    if ($('#socrata-flyout').length === 0) {
      const flyoutContent = $('<div>', {
        class: 'socrata-flyout-content',
      });

      const flyoutHint = $('<div>', {
        class: 'socrata-flyout-hint',
      });

      const flyout = $('<div>', {
        id: 'socrata-flyout',
        class: constructorOptions?.inheritTextStyle ? '' : 'built-in-text-style',
        popover: 'manual',
      }).append([flyoutContent, flyoutHint]);

      _flyout = flyout;
      _flyoutContent = flyoutContent;
      _flyoutHint = flyoutHint;

      $('body').append(flyout);
    } else {
      _flyout = $('#socrata-flyout');
      _flyoutContent = _flyout.find('.socrata-flyout-content');
      _flyoutHint = _flyout.find('.socrata-flyout-hint');
    }
  }

  /**
   * This is a jQuery event handler that responds to `.trigger()`, so the
   * second argument is expected and contains the payload of the event.
   */
  function _renderFlyoutData(options: RenderFlyoutOptions) {
    const { content, flyoutOffset, belowTarget } = options;
    // Check if this is coming from a Socrata table
    const isTableFlyout = $(options.element).closest('.socrata-table').length > 0;
    let rightSideHint = options.rightSideHint;
    const flyoutStyles = {
      left: 0,
      top: 0,
    };
    // Only `left` is computed in JS. Vertical placement is owned by the
    // directional CSS classes (southwest/southeast/northwest/northeast), so
    // we leave `top`/`bottom` alone here — writing them inline would beat
    // the SCSS rules and pin the hint to the wrong edge.
    const flyoutHintStyles = {
      left: 0,
    };
    let flyoutTargetBoundingClientRect;

    _flyoutContent.html(content);

    // Use $(window).width() instead of window.innerWidth because
    // the latter includes scrollbars depending on the browser.
    const windowWidth = _window.width() || 0;
    const flyoutWidth = _flyout.outerWidth(true) || 0;
    const flyoutHeight = _flyout.outerHeight(true) || 0;
    const flyoutHintHeight = _flyoutHint.outerHeight(true) || 0;

    // Set the left and top of flyout depending on its type
    if (flyoutOffset) {
      // Position the flyout so that the hint points to the current
      // location of the cursor.
      flyoutStyles.left = flyoutOffset.left;
      flyoutStyles.top = flyoutOffset.top - flyoutHeight - flyoutHintHeight - FLYOUT_BOTTOM_PADDING;
    } else {
      flyoutTargetBoundingClientRect = options.element.getBoundingClientRect();

      // Set the left of the flyout to the exact middle of the
      // target element.
      flyoutStyles.left = flyoutTargetBoundingClientRect.left + flyoutTargetBoundingClientRect.width / 2;

      // Set the top of the flyout, depending on whether the flyout
      // should be positioned above or below the target.
      if (belowTarget) {
        flyoutStyles.top = flyoutTargetBoundingClientRect.bottom + flyoutHintHeight + FLYOUT_TOP_PADDING;
      } else {
        flyoutStyles.top =
          flyoutTargetBoundingClientRect.top - (flyoutHeight + flyoutHintHeight) - FLYOUT_BOTTOM_PADDING;
      }
    }

    const flyoutRightEdge = flyoutStyles.left + flyoutWidth;
    const windowRightEdgeMinusPadding = windowWidth - FLYOUT_WINDOW_PADDING;

    // If the right edge of the flyout will be drawn off the right edge of
    // the screen, move the flyout to the left until its right edge is flush
    // with the right edge of the screen less the FLYOUT_WINDOW_PADDING.
    if (flyoutRightEdge >= windowRightEdgeMinusPadding) {
      // Adjust the position of the hint first so that it remains centered
      // over the element.
      flyoutHintStyles.left = flyoutStyles.left - (windowRightEdgeMinusPadding - flyoutWidth);
      // Then move the left edge of the flyout content over until it is
      // correctly positioned.
      flyoutStyles.left -= flyoutRightEdge - windowRightEdgeMinusPadding;
    }

    // If hint is at least halfway across the flyout, change its orientation.
    if (flyoutHintStyles.left > flyoutWidth / 2) {
      flyoutHintStyles.left = flyoutHintStyles.left - flyoutHintHeight;
      rightSideHint = true;
    }

    // If top of flyout is cut off by window, top-align the flyout.
    if (flyoutStyles.top < 0) {
      flyoutStyles.top = 0;
    }

    _flyout.toggleClass('dark', !!options.dark);

    // Only write `left` inline when JS shifted the hint (right-edge clipping
    // case). In the default case clear inline so SCSS owns the placement.
    _flyoutHint.css({ left: flyoutHintStyles.left || '' });

    _flyout
      .toggleClass('southwest', !rightSideHint && !belowTarget)
      .toggleClass('southeast', rightSideHint && !belowTarget)
      .toggleClass('northwest', !rightSideHint && belowTarget)
      .toggleClass('northeast', rightSideHint && belowTarget)
      .css(flyoutStyles);

    _showFlyout(isTableFlyout);
  }

  function _showFlyout(isTableFlyout = false) {
    if (showFlyoutTimer) {
      clearTimeout(showFlyoutTimer);
    }
    if (!_flyout) {
      return;
    }

    if (isTableFlyout) {
      showFlyoutTimer = window.setTimeout(() => {
        showFlyoutElement();
        showFlyoutTimer = null;
      }, 1500);
    } else {
      showFlyoutElement();
    }
  }

  function _hideFlyout() {
    if (showFlyoutTimer) {
      clearTimeout(showFlyoutTimer);
      showFlyoutTimer = null;
    }
    if (_flyout) {
      hideFlyoutElement();
    }
  }
}

export default FlyoutRenderer;
